import {
  Users,
  UserSkills,
} from '@/api/spotwrite';
import useConfig from './Config';
import useFormatFunc from './Formatter';

export default function useUsersFunc() {
  const config = useConfig();
  const formatter = useFormatFunc();

  /**
   * ログイン中の場合は true
   * @returns
   */
  function isLoggedIn() {
    return !!localStorage.getItem('accessToken');
  }
  /**
   * ログアウト
   * @returns
   */
  function doSignout() {
    if (isLoggedIn()) {
      localStorage.removeItem('accessToken');
      document.location.href = '/';
    }
  }

  /**
   * 自身の投稿作品のトータル数を返します
   * @param item
   * @returns
   */
  function getProductionsCount(item?: Users) {
    if (item && item._count) {
      return item._count.productions;
    }
    return 0;
  }

  /**
   * 自身の投稿作品のうち、サークル作品として投稿されたもののトータル数を返します
   * @param item
   * @returns
   */
  function countCirclePosts(item?: Users) {
    // TODO
    return 0;
  }

  /**
   * 自身の投稿作品に対するお気に入りのトータル数を返します
   * @param item
   * @returns
   */
  function countBookmarks(item?: Users) {
    if (item && item._count) {
      return item._count.favoriteProductions;
    }
    return 0;
  }

  /**
   * 自身の投稿作品に対するいいねのトータル数を返します
   * @param item
   * @returns
   */
  function countGoods(item?: Users) {
    if (item && item._count) {
      return item._count.goodProductions;
    }
    return 0;
  }

  /**
   * 自身の作品に対するコメントのトータル数を返します
   * @param item
   * @returns
   */
  function countComments(item?: Users) {
    if (item && item._count) {
      return item._count.productionComments;
    }
    return 0;
  }

  /**
   * 自身の作品に対するトータルの閲覧数を返します
   * @param item
   * @returns
   */
  function countTotalViews(item?: Users) {
    if (item && item.totalViewCount) {
      return item.totalViewCount;
    }
    return 0;
  }
  /**
   * アイコン画像URLを返します
   * @param item
   * @returns
   */
  function getIconUrl(item?: Users) {
    if (item && item.imageUrl) {
      const urlTemplate = config.IMAGE_URL_TEMPLATE();
      return urlTemplate.replace(':image', item.imageUrl);
    }
    return '';
  }

  /**
   * ニックネームを返します。
   * @param item
   * @returns
   */
  function getNickname(item?: Users) {
    if (item) {
      return item.nickname?.toString();
    }
    return '';
  }

  /**
   * メールアドレスを返します。
   * @param item
   * @returns
   */
  function getEmail(item?: Users) {
    if (item) {
      return item.email?.toString();
    }
    return '';
  }

  /**
   * 紹介文を返します。
   * @param item
   * @returns
   */
  function getIntroduction(item?: Users) {
    if (item) {
      return item.introduction?.toString();
    }
    return '';
  }

  /**
   * 地域を返します。
   * @param item
   * @returns
   */
  function getArea(item?: Users) {
    if (item) {
      return item.area?.toString();
    }
    return '';
  }

  /**
   * 地域を公開するかどうか
   * @param item
   * @returns
   */
  function isAreaPublished(item?: Users) {
    if (item) {
      return item.areaPub;
    }
    return false;
  }

  /**
   * 性別を文字列で返します
   * @param item
   * @returns
   */
  function getGenderName(item?: Users) {
    if (item) {
      switch (item.gender) {
        case 0:
          return '男性';
        case 1:
          return '女性';
        default:
          return 'その他';
      }
    }
    return '';
  }

  /**
   * 性別を公開するかどうか
   * @param item
   * @returns
   */
  function isGenderPublished(item?: Users) {
    if (item) {
      return item.genderPub;
    }
    return false;
  }

  /**
   * 生年月日を返します
   * @param item
   * @returns
   */
  function getBirthday(item?: Users) {
    if (item && item.birthday) {
      return formatter.formatDate(item.birthday);
    }
    return '';
  }

  /**
   * 生年月日を公開するかどうか
   * @param item
   * @returns
   */
  function isBirthdayPublished(item?: Users) {
    if (item) {
      return item.birthdayPub;
    }
    return false;
  }

  /**
   * twitterIdを返します
   * @param item
   * @returns
   */
  function getTwitterId(item?: Users) {
    if (item) {
      return item.twitterId?.toString();
    }
    return '';
  }

  /**
   * twitterを利用するかどうか
   * @param item
   * @returns
   */
  function useTwitter(item?: Users) {
    if (item) {
      return item.twitterUse === 1;
    }
    return false;
  }

  /**
   * facebookIdを返します
   * @param item
   * @returns
   */
  function getFacebookId(item?: Users) {
    if (item) {
      return item.facebookId?.toString();
    }
    return '';
  }

  /**
   * facebookを利用するかどうか
   * @param item
   * @returns
   */
  function useFacebook(item?: Users) {
    if (item) {
      return item.facebookUse === 1;
    }
    return false;
  }

  /**
   * ソーシャルアカウントの利用が1つ以上ある場合はtrue
   * @param item
   * @returns
   */
  function hasSocialAccounts(item?: Users) {
    return useFacebook(item) || useTwitter(item);
  }

  /**
   * スキルの一覧を取得
   * @param item
   * @returns
   */
  function getSkills(item?: Users) {
    const res: UserSkills[] = [];
    if (item && item.userSkills && item.userSkills.length > 0) {
      item.userSkills.forEach((userSkill) => {
        if (userSkill && userSkill.skill) {
          res.push(userSkill);
        }
      });
    }
    return res;
  }

  return {
    isLoggedIn,
    getSkills,
    getTwitterId,
    useTwitter,
    getFacebookId,
    useFacebook,
    hasSocialAccounts,
    getBirthday,
    isBirthdayPublished,
    getIconUrl,
    getNickname,
    getIntroduction,
    getArea,
    isAreaPublished,
    getGenderName,
    isGenderPublished,
    getEmail,
    getProductionsCount,
    countBookmarks,
    countGoods,
    countComments,
    countTotalViews,
    countCirclePosts,
    doSignout,
  };
}
